import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import Lenis from 'lenis'

gsap.registerPlugin(ScrollTrigger, SplitText)

const DEFAULTS = {
  duration: 1.1,
  ease: 'power3.out',
  start: 'top 80%',
}

class Animations {
  constructor() {
    this.init()
  }

  init() {
    this.scrollJacking()
    this.heroHome()
    this.hero()
    this.videoText()
    this.cardTeasers()
    this.scrollFadeUp()
    this.scrollStagger()
    this.imageReveal()
    this.carousel()
    this.image()
    this.imageText()
    this.imageTextTeaser()
    this.imageTextTeaserParallax()
    this.teamFilter()
    this.teamManual()
    this.ctaTeaser()
    this.text()
    this.accordion()
  }

  scrollJacking() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const lenis = new Lenis({
      duration: 1.4,           // higher = longer, more fluid glide (default is ~1.2)
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // custom easeOutExpo-ish curve
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.9,    // slightly dampened = less "snappy", more weighty
      touchMultiplier: 1.5,
      infinite: false,
    });

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);
  }

  heroHome() {
    const el = document.querySelector('.hero-home')
    if (!el) return

    const media = el.querySelector('.hero-home__figure')
    const heading = el.querySelector('.hero-home__heading')
    const text = el.querySelector('.hero-home__text')

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: DEFAULTS.start,
        once: true,
      },
    })

    if (media) {
      tl.to(media, {
        opacity: 1,
        duration: 2,
        ease: 'power2.inOut',
      }, .5) // starts immediately when timeline begins
    }

    if (heading) {
      this._animateWords(heading, tl, media ? '-=1' : 0, { duration: 1, stagger: 0.18, ease: 'power2.inOut', x: 0 })
    }

    if (text) {
      tl.to(text, {
        opacity: 1,
        y: 0,
        duration: 1.6,
        ease: 'power2.out',
      },  '+=0') // starts immediately when timeline begins
    }
  }

  hero() {
    const heroes = [...document.querySelectorAll('.hero')]

    heroes.forEach(el => {
      const heading = el.querySelector('.hero__heading')
      const text = el.querySelector('.hero__text')
      const media = el.querySelector('.hero__media')

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: DEFAULTS.start,
          once: true,
        },
      })

      if (media) {
        tl.to(media, {
          opacity: 1,
          duration: 1.3,
          ease: 'power3.out',
        }, 1.2) // starts immediately when timeline begins
      }

      if (heading) {
        this._animateWords(heading, tl, media ? '-=0.7' : 0, { duration: 0.7, stagger: 0.05 })
      }

      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          duration: 1.3,
          ease: 'power3.out',
        }, '+=0.2')
      }
    })
  }

  ctaTeaser() {
    const blocks = [...document.querySelectorAll('.cta-teaser')]

    blocks.forEach(el => {
      const heading = el.querySelector('.heading')
      const text = el.querySelector('.text-block')

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: 'top+=96 80%',
          once: true,
        },
      })

      if (heading) {
        this._animateWords(heading, tl, 0, { duration: 0.7, stagger: 0.05 })
      }

      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          duration: 1.3,
          ease: 'power3.out',
        }, '+=0.2') // starts immediately when timeline begins
      }
    })
  }

  videoText() {
    const blocks = [...document.querySelectorAll('.video-text')]

    blocks.forEach(el => {
      const media = el.querySelector('.video-text__media')
      const heading = el.querySelector('.video-text__text > :first-child')
      const text = el.querySelector('.text-block')

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: DEFAULTS.start,
          once: true,
        },
      })

      if (heading) {
        this._animateWords(heading, tl, 0, { duration: 0.7, stagger: 0.05 })
      }

      if (media) {
        tl.to(media, {
          opacity: 1,
          scale: 1,
          duration: DEFAULTS.duration,
          ease: DEFAULTS.ease,
        }, '+=0')
      }

      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          duration: DEFAULTS.duration,
          ease: 'power3.out',
        }, '+=0')
      }
    })
  }

  // Splits an element into words and staggers them left to right.
  _animateWords(el, tl, position, { duration = 0.75, stagger = 0.04, x = -8, ease = 'power3.out'} = {}) {
    const split = new SplitText(el, { type: 'words', wordsClass: 'split-word' })

    tl.set(el, {
      opacity: 1,
    }, position)

    tl.from(split.words, {
      opacity: 0,
      x,
      duration,
      ease,
      stagger,
    }, position)
  }

  cardTeasers() {
    const grids = [...document.querySelectorAll('.card-teasers')]

    grids.forEach(grid => {

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: grid,
          start: DEFAULTS.start,
          once: true,
        },
      })

      const heading = grid.querySelector('.heading')
      if (heading) {
        this._animateWords(heading, tl, 0, { duration: 0.7, stagger: 0.05 })
      }

      const items = [...grid.querySelectorAll('.card-teasers__item')]
      if (!items.length) return

      gsap.from(items, {
        opacity: 0,
        y: 30,
        duration: DEFAULTS.duration,
        ease: DEFAULTS.ease,
        stagger: 0.1,
        scrollTrigger: {
          trigger: grid,
          start: DEFAULTS.start,
          once: true,
        },
      })
    })
  }

  // Generic scroll animation via [data-animate] attribute.
  // Supported values: fade-up (default), fade-down, fade-in, slide-left, slide-right
  // Optional: [data-animate-delay="0.2"] for a delay in seconds
  scrollFadeUp() {
    const elements = [...document.querySelectorAll('[data-animate]')]

    elements.forEach(el => {
      const type = el.dataset.animate || 'fade-up'
      const delay = parseFloat(el.dataset.animateDelay) || 0

      gsap.from(el, {
        ...this._fromVars(type),
        duration: DEFAULTS.duration,
        ease: DEFAULTS.ease,
        delay,
        scrollTrigger: {
          trigger: el,
          start: DEFAULTS.start,
          once: true,
        },
      })
    })
  }

  // Staggers direct children of [data-animate-stagger] elements.
  // Optional: [data-animate-stagger="0.15"] overrides the default 0.1s stagger interval
  scrollStagger() {
    const groups = [...document.querySelectorAll('[data-animate-stagger]')]

    groups.forEach(group => {
      const items = [...group.children]
      if (!items.length) return

      const interval = parseFloat(group.dataset.animateStagger) || 0.1

      gsap.from(items, {
        opacity: 0,
        y: 30,
        duration: DEFAULTS.duration,
        ease: DEFAULTS.ease,
        stagger: interval,
        scrollTrigger: {
          trigger: group,
          start: DEFAULTS.start,
          once: true,
        },
      })
    })
  }

  // Clip-path image reveal for elements marked with [data-animate-image].
  imageReveal() {
    const images = [...document.querySelectorAll('.img--animate')]

    images.forEach(img => {
      gsap.to(img, {
        opacity: 1,
        duration: 1.6,
        ease: DEFAULTS.ease,
        scrollTrigger: {
          trigger: img,
          start: 'top 60%',
          once: true,
        },
      })
    })
  }

  accordion() {
    const blocks = [...document.querySelectorAll('.accordions')]
    blocks.forEach(el => {
      gsap.fromTo(el,
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1.6,
            ease: 'power2.inOut',
            scrollTrigger: {
              trigger: el,
              start: 'top+=32 80%',
              once: true,
            },
          }
      )
    })

    // Closing keeps :focus on the summary; clear it on leave so hover can apply again
    document.querySelectorAll('.accordion__header').forEach(header => {
      header.addEventListener('mouseleave', () => {
        if (document.activeElement === header) header.blur()
      })
    })
  }

  image() {
    const blocks = [...document.querySelectorAll('.cmp-image-section')]

    blocks.forEach(el => {
      const heading = el.querySelector('.heading')
      if (!heading) return

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: DEFAULTS.start,
          once: true,
        },
      })

      this._animateWords(heading, tl, 0, { duration: 0.7, stagger: 0.05 })
    })
  }

  text() {
    const blocks = [...document.querySelectorAll('.rich-content')]

    blocks.forEach(el => {
      ;[...el.children].forEach(child => {
        gsap.from(child, {
          opacity: 0,
          y: 30,
          duration: DEFAULTS.duration,
          ease: DEFAULTS.ease,
          scrollTrigger: {
            trigger: child,
            start: DEFAULTS.start,
            once: true,
          },
        })
      })
    })
  }

  imageText() {
    const blocks = [...document.querySelectorAll('.image-text')]

    blocks.forEach(el => {
      const heading = el.querySelector('.heading')
      const text = el.querySelector('.text-block')

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: DEFAULTS.start,
          once: true,
        },
      })

      if (heading) {
        this._animateWords(heading, tl, 0, { duration: 0.7, stagger: 0.05 })
      }

      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          duration: 1.3,
          ease: 'power3.out',
        }, '+=0.1')
      }
    })
  }

  imageTextTeaser() {
    const blocks = [...document.querySelectorAll('.image-text-teaser')]

    blocks.forEach(el => {
      const heading = el.querySelector('.heading')
      const cta = el.querySelector('.cta')
      const text = el.querySelector('.text-block')

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          once: true,
        },
      })

      if (heading) {
        this._animateWords(heading, tl, 0, { duration: 0.7, stagger: 0.05 })
      }

      if (cta) {
        tl.to(cta, {
          opacity: 1,
          y: 0,
          duration: 1.3,
          ease: 'power3.out',
        }, '+=0.1') // starts immediately when timeline begins
      }

      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          duration: 1.3,
          ease: 'power3.out',
        }, '+=0.1') // starts immediately when timeline begins
      }
    })
  }

  imageTextTeaserParallax() {
    const blocks = [...document.querySelectorAll('.image-text-teaser')]

    const LIMIT = 14
    const ATTRACT_RADIUS = 200 // px from component edge at which attraction begins

    blocks.forEach(block => {
      const small = block.querySelector('.figure-image-text-teaser-small')
      const large = block.querySelector('.figure-image-text-teaser')
      const images = [small, large].filter(Boolean)
      if (!images.length) return

      let isActive = false

      document.addEventListener('mousemove', e => {
        const rect = block.getBoundingClientRect()

        // Nearest point on the component rect to the cursor
        const clampedX = Math.max(rect.left, Math.min(rect.right, e.clientX))
        const clampedY = Math.max(rect.top, Math.min(rect.bottom, e.clientY))
        const distX = e.clientX - clampedX
        const distY = e.clientY - clampedY
        const distance = Math.sqrt(distX * distX + distY * distY)

        if (distance > ATTRACT_RADIUS) {
          if (isActive) {
            isActive = false
            gsap.to(images, { x: 0, y: 0, duration: 1.2, ease: 'expo.out' })
          }
          return
        }

        isActive = true

        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const dx = (e.clientX - cx) / (rect.width / 2)
        const dy = (e.clientY - cy) / (rect.height / 2)

        // Smoothly ramp up influence: 0 at ATTRACT_RADIUS, 1 at component edge / inside
        const influence = 1 - Math.min(distance / ATTRACT_RADIUS, 1)

        if (small) {
          gsap.to(small, {
            x: dx * LIMIT * influence,
            y: dy * LIMIT * influence,
            duration: 0.8,
            ease: 'power2.out',
          })
        }

        if (large) {
          gsap.to(large, {
            x: dx * LIMIT * 0.55 * influence,
            y: dy * LIMIT * 0.55 * influence,
            duration: 0.8,
            ease: 'power2.out',
          })
        }
      })
    })
  }

  carousel() {
    const carousels = [...document.querySelectorAll('.slide--carousel')]

    carousels.forEach(el => {
      gsap.from(el, {
        opacity: 0,
        x: -60,
        duration: DEFAULTS.duration,
        ease: DEFAULTS.ease,
        scrollTrigger: {
          trigger: el,
          start: DEFAULTS.start,
          once: true,
        },
      })
    })
  }

  teamFilter() {
    const revealTeams = (teams) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          teams.forEach((team, i) => {
            team.style.transitionDelay = `${i * 50}ms`
            team.classList.add('loaded')
          })
        })
      })
    }

    document.addEventListener('htmx:afterSwap', (event) => {
      const element = event.srcElement
      if (element.id !== 'team-filter') return
      revealTeams([...element.querySelectorAll('.team')])
    })
  }

  teamManual() {
    const grid = document.querySelector('.teams__grid--manual')
    if (!grid) return

    const teams = [...grid.querySelectorAll('.team')]
    if (!teams.length) return

    ScrollTrigger.create({
      trigger: grid,
      start: DEFAULTS.start,
      once: true,
      onEnter: () => {
        teams.forEach((team, i) => {
          team.style.transitionDelay = `${i * 50}ms`
          team.classList.add('loaded')
        })
      },
    })
  }

  _fromVars(type) {
    const map = {
      'fade-up': { opacity: 0, y: 30 },
      'fade-down': { opacity: 0, y: -30 },
      'fade-in': { opacity: 0 },
      'slide-left': { opacity: 0, x: 40 },
      'slide-right': { opacity: 0, x: -40 },
    }

    return map[type] ?? map['fade-up']
  }
}

export default Animations